import { api } from "@/lib/api/client";
import type { components } from "@/types/api";

export type AccessKey = components["schemas"]["AccessKey"];

/**
 * GET /v1/access-keys
 * The key string comes back masked; only creation returns it in full.
 */
export function listAccessKeys(): Promise<{ accessKeys: AccessKey[] }> {
  return api.get<{ accessKeys: AccessKey[] }>("/v1/access-keys");
}

/**
 * POST /v1/access-keys
 * Returns the raw key in `accessKey.name`, visible only in this response.
 * `ttl` is milliseconds; the server defaults to 60 days when omitted.
 */
export function createAccessKey(body: {
  friendlyName: string;
  ttl?: number;
}): Promise<{ accessKey: AccessKey }> {
  return api.post<{ accessKey: AccessKey }>("/v1/access-keys", body);
}

/**
 * PATCH /v1/access-keys/:friendlyName
 */
export function renameAccessKey(
  friendlyName: string,
  newFriendlyName: string,
): Promise<{ accessKey: AccessKey }> {
  return api.patch<{ accessKey: AccessKey }>(
    `/v1/access-keys/${encodeURIComponent(friendlyName)}`,
    { friendlyName: newFriendlyName },
  );
}

/**
 * DELETE /v1/access-keys/:friendlyName
 */
export function deleteAccessKey(friendlyName: string): Promise<void> {
  return api.delete<void>(`/v1/access-keys/${encodeURIComponent(friendlyName)}`);
}

/**
 * DELETE /v1/sessions/:createdBy
 * Removes every session key created from the given device or address.
 */
export function deleteSessions(createdBy: string): Promise<void> {
  return api.delete<void>(`/v1/sessions/${encodeURIComponent(createdBy)}`);
}

/**
 * DELETE /v1/sessions
 * Revokes every browser session of the account, this one included. CLI machines are
 * untouched; they have their own section.
 */
export function deleteAllSessions(): Promise<void> {
  return api.delete<void>("/v1/sessions");
}
